<template>
  <div class="pin-card">
    <div class="pin-header">
      <div class="pin-icon">
        <i class="el-icon-collection-tag"></i>
        <span>Pinned Stack</span>
        <span class="dot">•</span>
        <span class="author-wrapper">
          <span class="author-label">by</span>
          <el-avatar :size="16" :src="pin.authorAvatar" class="author-avatar" style="font-size: 10px;">{{ pin.author?.charAt(0) || 'U' }}</el-avatar>
          <span class="author-name">{{ pin.author }}</span>
        </span>
        <span class="dot">•</span>
        <span class="timestamp">{{ formattedTimestamp }}</span>
      </div>
      <div class="pin-actions">
        <el-button
          type="text"
          size="mini"
          icon="el-icon-view"
          @click="viewPost"
          class="view-btn"
        >
          View the Stack
        </el-button>
        <el-dropdown trigger="click" @command="handleCommand">
          <el-button type="text" size="mini" class="more-btn">
            <i class="el-icon-more"></i>
          </el-button>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item command="remove">
              <i class="el-icon-delete"></i> Remove
            </el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
      </div>
    </div>
    
    <div class="pin-content">
      <div class="post-context">
        <h4 class="post-title" @click="viewPost">{{ pin.postTitle }}</h4>
      </div>
      
      <!-- Handle multiple pinned answers -->
      <div v-if="pin.pinned_answers && pin.pinned_answers.length > 0" class="pinned-answers">
        <div v-for="(pinnedAnswer, index) in pin.pinned_answers" :key="pinnedAnswer.id" class="answer-preview">
          <div class="answer-header">
            <span class="answer-label-text">Pinned Card</span>
            <span class="dot">•</span>
            <span class="answer-index">Card #{{ pinnedAnswer.answerIndex + 1 }}</span>
          </div>

          <div class="answer-content">
            <p v-html="getDisplayContent(pinnedAnswer)"></p>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
export default {
  name: 'PinCard',
  props: {
    pin: {
      type: Object,
      required: true
    }
  },
  computed: {
    formattedTimestamp() {
      const date = new Date(this.pin.timestamp)
      const now = new Date()
      const diffInHours = Math.floor((now - date) / (1000 * 60 * 60))
      
      if (diffInHours < 1) {
        return 'Just now'
      } else if (diffInHours < 24) {
        return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`
      } else {
        const diffInDays = Math.floor(diffInHours / 24)
        return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`
      }
    }
  },
  methods: {
    viewPost() {
      if (this.pin.collection_id) {
        this.$router.push({
          path: '/read-collection',
          query: { collection_id: this.pin.collection_id }
        })
      } else {
        this.$router.push({
          path: '/stack',
          query: { id: this.pin.id }
        })
      }
    },
    getDisplayContent(pinnedAnswer) {
      if (pinnedAnswer.contentType === 'text') {
        return pinnedAnswer.content || 'No content available'
      } else if (pinnedAnswer.contentType === 'quiz') {
        return pinnedAnswer.quiz_content || 'No content available'
      } else {
        return 'No content available'
      }
    },
    handleCommand(command) {
      if (command === 'remove') {
        this.$emit('remove', this.pin.id)
      }
    }
  }
}
</script>

<style scoped lang="scss">
.pin-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-left: 3px solid #1159AF;
  border-radius: 8px;
  padding: 16px;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-color: #dee2e6;
  }
}

.pin-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.pin-icon {
  display: flex;
  align-items: center;
  gap: 8px;

  i {
    font-size: 18px;
    color: #1159AF;
  }

  > span {
    font-weight: 600;
    color: #1159AF;
    font-size: 14px;
  }

  .dot {
    color: #adb5bd;
    font-size: 14px;
    font-weight: normal;
  }

  .author-wrapper {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    color: #6c757d;
    font-weight: normal;

    .author-label {
      font-size: 12px;
    }

    .author-avatar {
      flex-shrink: 0;
    }

    .author-name {
      font-size: 12px;
    }
  }

  .timestamp {
    font-size: 12px;
    color: #6c757d;
    font-weight: normal;
  }
}

.pin-actions {
  display: flex;
  gap: 8px;
  
  .view-btn {
    color: #1159AF;
    font-size: 12px;
    
    &:hover {
      color: #409EFF;
    }
  }
  
  .remove-btn {
    color: #6c757d;
    font-size: 12px;
    
    &:hover {
      color: #dc3545;
    }
  }
}

.pin-content {
  .post-context {
    margin-bottom: 12px;
    
    .post-title {
      font-size: 16px;
      font-weight: 600;
      color: #2c3e50;
      margin: 0 0 6px 0;
      cursor: pointer;
      line-height: 1.3;
      word-wrap: normal;
      word-break: normal;
      overflow-wrap: normal;
      hyphens: none;
      
      &:hover {
        color: #1159AF;
      }
    }
    
    .pin-meta {
      font-size: 12px;
      color: #6c757d;
    }
  }
  
  .answer-preview {
    background: #f8f9fa;
    border-radius: 6px;
    padding: 12px;
    
    .answer-header {
      display: flex;
      align-items: center;
      gap: 6px;
      margin-bottom: 8px;

      .answer-label-text {
        font-size: 12px;
        font-weight: 600;
        color: #f39c12;
      }

      .dot {
        color: #adb5bd;
        font-size: 12px;
      }

      .answer-index {
        background-color: #fff;
        padding: 2px 6px;
        border-radius: 4px;
        font-weight: 500;
        font-size: 12px;
        color: #6c757d;
      }
    }
    
    .answer-content {
      margin-bottom: 8px;
      font-size: 14px;
      color: #495057;
      line-height: 1.5;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;

      p {
        margin: 0;
        display: inline;
        white-space: nowrap;
      }
    }
    
    .answer-author {
      font-size: 12px;
      color: #6c757d;
      font-style: italic;
    }
  }
}
</style> 